/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { Button, IconButton, InputAdornment, TextField } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateField } from '@mui/x-date-pickers/DateField';
import { PatternFormat } from 'react-number-format';
import DefaultModal from './DefaultModal';
import { passwordRequirementsMsg } from '../../common/constants';
import { isStrEmptyOrNull, isValidPassword, isValidReenterField, isValidLoanNo } from '../../common/UtilityFunctions';
import Styles from '../../css/modals/PasswordSecretQuestionModal.module.css';
import { useResetPasswordContext } from '../../contexts/ResetPasswordContext';

const PasswordSecretQuestionModal = () => {
  const resetPasswordContext = useResetPasswordContext();

  return (
    <DefaultModal
      open={resetPasswordContext.passwordSecretQuestionModalOpen}
      title={''}
      content={
        <div className={Styles.section}>
          <p className={Styles.boldText}>
            {isStrEmptyOrNull(resetPasswordContext.secretQuestion)
              ? 'Please enter your new password.'
              : 'Please answer the Secret Question and enter your new Password.'}
          </p>
          <div className={Styles.gridContainer}>
            <div>
              {!isStrEmptyOrNull(resetPasswordContext.secretQuestion) &&
                resetPasswordContext.questionField === 'lastName' && (
                  <TextField
                    fullWidth
                    size='small'
                    label={resetPasswordContext.secretQuestion}
                    placeholder={resetPasswordContext.secretQuestionPlaceholder}
                    error={resetPasswordContext.secretAnswerError}
                    helperText={
                      resetPasswordContext.secretAnswerError ? resetPasswordContext.secretAnswerErrorMsg : ' '
                    }
                    value={resetPasswordContext.secretAnswer}
                    type={resetPasswordContext.showSecretAnswer ? 'text' : 'password'}
                    onChange={(e) => {
                      resetPasswordContext.setSecretAnswer(e.target.value);
                      resetPasswordContext.setSecretAnswerError(isStrEmptyOrNull(e.target.value));
                      resetPasswordContext.setSecretAnswerErrorMsg(
                        isStrEmptyOrNull(e.target.value) ? 'Please input your Last Name.' : ' ',
                      );
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label='toggle password visibility'
                            onClick={resetPasswordContext.handleClickShowSecretAnswer}
                            onMouseDown={resetPasswordContext.handleMouseDownSecretAnswer}
                          >
                            {resetPasswordContext.showSecretAnswer ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              {!isStrEmptyOrNull(resetPasswordContext.secretQuestion) &&
                resetPasswordContext.questionField === 'loanNo' && (
                  <PatternFormat
                    fullWidth
                    size='small'
                    label={resetPasswordContext.secretQuestion}
                    placeholder={resetPasswordContext.secretQuestionPlaceholder}
                    customInput={TextField}
                    format='############'
                    error={resetPasswordContext.secretAnswerError}
                    helperText={
                      resetPasswordContext.secretAnswerError ? resetPasswordContext.secretAnswerErrorMsg : ' '
                    }
                    value={resetPasswordContext.secretAnswer}
                    type={resetPasswordContext.showSecretAnswer ? 'text' : 'password'}
                    onChange={(e) => {
                      resetPasswordContext.setSecretAnswer(e.target.value);
                      resetPasswordContext.setSecretAnswerError(!isValidLoanNo(e.target.value));
                      resetPasswordContext.setSecretAnswerErrorMsg(
                        isStrEmptyOrNull(e.target.value)
                          ? 'Please input your Loan Number.'
                          : 'Loan Number must be 12 digits.',
                      );
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label='toggle password visibility'
                            onClick={resetPasswordContext.handleClickShowSecretAnswer}
                            onMouseDown={resetPasswordContext.handleMouseDownSecretAnswer}
                          >
                            {resetPasswordContext.showSecretAnswer ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              {!isStrEmptyOrNull(resetPasswordContext.secretQuestion) &&
                resetPasswordContext.questionField === 'dob' && (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateField
                      disableFuture
                      size='small'
                      required
                      label={resetPasswordContext.secretQuestion}
                      value={resetPasswordContext.secretAnswerDate}
                      onChange={resetPasswordContext.setSecretAnswerDate}
                      slotProps={{
                        textField: {
                          helperText: resetPasswordContext.secretAnswerError ? 'Please input your Date of Birth.' : ' ',
                          error: resetPasswordContext.secretAnswerError,
                        },
                      }}
                    />
                  </LocalizationProvider>
                )}
            </div>
            <section />
            <div>
              <TextField
                fullWidth
                size='small'
                label='New Password *'
                placeholder='New Password *'
                error={resetPasswordContext.passwordError}
                helperText={resetPasswordContext.passwordError ? resetPasswordContext.passwordErrorMsg : ' '}
                value={resetPasswordContext.password}
                type={resetPasswordContext.showPassword ? 'text' : 'password'}
                onChange={(e) => {
                  resetPasswordContext.handleUpdatePassword(e.target.value);
                  resetPasswordContext.setPasswordError(!isValidPassword(e.target.value));
                  resetPasswordContext.setPasswordErrorMsg(
                    isStrEmptyOrNull(e.target.value)
                      ? 'Please enter your new Password.'
                      : 'Password does not meet criteria.',
                  );
                  // do not display Re-enter Password error if the user hasn't changed the Re-enter field yet
                  resetPasswordContext.setReenterPasswordError(
                    !resetPasswordContext.reenterPasswordChanged
                      ? false
                      : !isValidReenterField(e.target.value, resetPasswordContext.reenterPassword),
                  );
                }}
                onFocus={(e) => {
                  resetPasswordContext.setPasswordFocus(true);
                }}
                onBlur={(e) => {
                  resetPasswordContext.setPasswordFocus(false);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        aria-label='toggle password visibility'
                        className={resetPasswordContext.passwordFocus ? '' : Styles.passwordNoFocus}
                        onClick={resetPasswordContext.handleClickShowPassword}
                        onMouseDown={resetPasswordContext.handleMouseDownPassword}
                      >
                        {resetPasswordContext.showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>
            <TextField
              fullWidth
              size='small'
              label='Re-enter Password *'
              placeholder='Re-enter Password *'
              error={resetPasswordContext.reenterPasswordError}
              helperText={
                resetPasswordContext.reenterPasswordError ? resetPasswordContext.reenterPasswordErrorMsg : ' '
              }
              value={resetPasswordContext.reenterPassword}
              type={resetPasswordContext.showReenterPassword ? 'text' : 'password'}
              onChange={(e) => {
                resetPasswordContext.handleUpdateReenterPassword(e.target.value);
                resetPasswordContext.setReenterPasswordError(
                  !isValidReenterField(resetPasswordContext.password, e.target.value),
                );
                resetPasswordContext.setReenterPasswordErrorMsg(
                  isStrEmptyOrNull(e.target.value) ? 'Please re-enter your new Password.' : 'Passwords do not match.',
                );
                resetPasswordContext.setReenterPasswordChanged(true);
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label='toggle password visibility'
                      onClick={resetPasswordContext.handleClickShowReenterPassword}
                      onMouseDown={resetPasswordContext.handleMouseDownReenterPassword}
                    >
                      {resetPasswordContext.showReenterPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <p className={Styles.text}>{passwordRequirementsMsg}</p>
          </div>
        </div>
      }
      footer={
        <div className={Styles.footer}>
          <Button
            variant='contained'
            onClick={() => {
              resetPasswordContext.setPasswordSecretQuestionModalOpen(false);
              resetPasswordContext.clearSecretQuestionFields();
            }}
            className={Styles.button}
          >
            Cancel
          </Button>
          <Button
            variant='contained'
            className={Styles.button}
            onClick={() => {
              resetPasswordContext.validateSecretQuestionFields();
            }}
          >
            Submit
          </Button>
        </div>
      }
    />
  );
};

export default PasswordSecretQuestionModal;
